import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 21 — Executive Presence Workspace profile.
 * Mirrors practiceQueries.js's `request()` error contract exactly.
 * Every route below takes an explicit `memberId` so the SAME hooks work
 * for the owning client's own Presence Lab (memberId = their own
 * `viewer_membership_id` from useWorkspaceHome) and for the coach's
 * Client 360 "Presence" tab (memberId = the URL's client id) — never a
 * second, duplicated set of routes/hooks for the two views.
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });
const jsonPatch = (path, payload) => request(path, { method: "PATCH", body: JSON.stringify(payload ?? {}) });

const base = (wrkId, memberId) => `/api/rehearsal-room/workspaces/${wrkId}/members/${memberId}/presence`;

export function usePresenceContexts(workspaceId, memberId) {
  return useQuery({
    queryKey: ["presence-contexts", workspaceId, memberId],
    enabled: !!workspaceId && !!memberId,
    queryFn: () => request(`${base(workspaceId, memberId)}/contexts`),
  });
}

export function usePresenceContextDetail(workspaceId, memberId, pscId) {
  return useQuery({
    queryKey: ["presence-context-detail", workspaceId, memberId, pscId],
    enabled: !!workspaceId && !!memberId && !!pscId,
    queryFn: () => request(`${base(workspaceId, memberId)}/contexts/${pscId}`),
  });
}

export function useCreatePresenceContext(workspaceId, memberId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`${base(workspaceId, memberId)}/contexts`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presence-contexts", workspaceId, memberId] }),
  });
}

export function useUpdatePresenceContext(workspaceId, memberId, pscId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPatch(`${base(workspaceId, memberId)}/contexts/${pscId}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["presence-contexts", workspaceId, memberId] });
      qc.invalidateQueries({ queryKey: ["presence-context-detail", workspaceId, memberId, pscId] });
    },
  });
}

export function useCreatePresenceFocus(workspaceId, memberId, pscId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`${base(workspaceId, memberId)}/contexts/${pscId}/foci`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presence-context-detail", workspaceId, memberId, pscId] }),
  });
}

export function useUpdatePresenceFocus(workspaceId, memberId, pscId, psfId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPatch(`${base(workspaceId, memberId)}/foci/${psfId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presence-context-detail", workspaceId, memberId, pscId] }),
  });
}

export function useCreatePresenceApplication(workspaceId, memberId, pscId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`${base(workspaceId, memberId)}/contexts/${pscId}/applications`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presence-context-detail", workspaceId, memberId, pscId] }),
  });
}

// Self-scoped only — reflections belong to the caller's own membership.
export function usePresenceReflections(workspaceId) {
  return useQuery({
    queryKey: ["presence-reflections", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/presence/reflections`),
  });
}
